import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePlanningPeriod } from "@/context/PlanningPeriodContext";
import { PlanningPeriodSelector } from "@/components/PlanningPeriodSelector";
import { STATUS } from "@/lib/status";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const SEGMENTS = [
  { key: "preferred", label: STATUS.preferred.label, dot: STATUS.preferred.dot },
  { key: "available", label: STATUS.available.label, dot: STATUS.available.dot },
  { key: "reluctant", label: STATUS.reluctant.label, dot: STATUS.reluctant.dot },
  { key: "unavailable", label: STATUS.unavailable.label, dot: STATUS.unavailable.dot },
  { key: "not_filled", label: "Nog niet ingevuld", dot: "bg-slate-300" },
];

export default function AvailabilityStatus() {
  const { query } = usePlanningPeriod();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/availability/response-status?${query}`)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query]);

  return (
    <div className="space-y-6" data-testid="availability-status-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Beschikbaarheidsstatus</h1>
        <p className="text-muted-foreground mt-1">Zie per medewerker hoe ver de beschikbaarheid is ingevuld voor de gekozen periode.</p>
      </div>

      <PlanningPeriodSelector />

      <div className="flex flex-wrap gap-4" data-testid="availability-status-legend">
        {SEGMENTS.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <span className={`w-3 h-3 rounded-sm ${s.dot}`} /> {s.label}
          </span>
        ))}
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : data.total_services === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="availability-status-empty">
          Er zijn geen diensten in deze periode. Kies een andere periode of voeg eerst diensten toe.
        </Card>
      ) : data.rows.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="availability-status-no-members">
          Er zijn nog geen actieve medewerkers om te tonen.
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-40">Medewerker</TableHead>
                <TableHead>Verdeling over {data.total_services} diensten</TableHead>
                <TableHead className="text-right w-28">Ingevuld</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => {
                const pct = Math.round((r.answered / data.total_services) * 100);
                return (
                  <TableRow key={r.user_id} data-testid={`avail-status-row-${r.user_id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>
                      <div className="flex h-4 w-full min-w-48 rounded-full overflow-hidden bg-slate-100" data-testid={`avail-status-bar-${r.user_id}`}>
                        {SEGMENTS.map((s) => {
                          const v = r[s.key];
                          if (!v) return null;
                          return (
                            <div
                              key={s.key}
                              className={`${s.dot} transition-[width] duration-300`}
                              style={{ width: `${(v / data.total_services) * 100}%` }}
                              title={`${s.label}: ${v}`}
                              data-testid={`avail-status-seg-${r.user_id}-${s.key}`}
                            />
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold font-heading" data-testid={`avail-status-pct-${r.user_id}`}>{pct}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
